// Copyright © 2021 Move Closer
import { OverrideStrategy, RelatedType } from '../contracts';
import { AbstractTypeDriver } from './abstract';
/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export class SetDriver extends AbstractTypeDriver {
    constructor() {
        super(...arguments);
        /**
         * Default limit of set.
         * @protected
         */
        this.defaultLimit = 10;
        /**
         * Current position of list pointer.
         * @protected
         */
        this.positions = {};
        /**
         * Key that stores RelatedType.
         * @protected
         */
        this.typeKey = RelatedType.Set;
    }
    /**
     * @inheritDoc
     */
    reset() {
        this.positions = {};
    }
    /**
     * @inheritDoc
     */
    resolve(id, record, config, driverResolver) {
        this.throwWhenMissing(id, record);
        const set = this.describe(id, record);
        // Check if the output is as expected.
        if (!Array.isArray(set.items)) {
            throw new Error(`[RelatedService]: Failed to resolve items for the list of a given ID [${id}]!`);
        }
        let contentToFind = this.considerOverrides(set.items, config.overrides);
        // Let's we need to consider an entry conditions. If we have limit set,
        // should we prevent against duplicates & if the pointer should be moved.
        const limit = !!config.take && typeof config.take === 'number' ? Math.floor(config.take) : this.defaultLimit;
        const continueOrder = typeof config.continueOrder === 'undefined' || config.continueOrder;
        // In case of limit let's narrow the range.
        const startPosition = continueOrder ? this.getPositionOfSet(id) : 0;
        const endPosition = startPosition + limit;
        contentToFind = contentToFind.filter((el, index) => {
            return startPosition <= index && index < endPosition;
        });
        const contentDriver = driverResolver(RelatedType.Content);
        const toReturn = contentToFind.reduce((acc, curr) => {
            if (!contentDriver.canDescribe(`${curr}`, record)) {
                return acc;
            }
            acc.push(contentDriver.resolve(`${curr}`, record, config, driverResolver));
            return acc;
        }, []);
        this.setPositionOfSet(id, endPosition);
        return toReturn;
    }
    /**
     * @protected
     */
    considerOverrides(items, overrides) {
        if (typeof overrides === 'undefined') {
            return items;
        }
        // Store the information about the original length of the array.
        const originalLength = items.length;
        // Loop through all the IDs and check if any of them should be replaced.
        const ids = items.reduce((acc, curr, index) => {
            // Find the override config for the currently processed ID.
            const overrideConfig = overrides.find(config => config.index === index);
            // If the item doesn't have any overrides configured,
            // return everything from previous iterations, plus current ID.
            if (typeof overrideConfig === 'undefined') {
                return [...acc, curr];
            }
            const { strategy, to } = overrideConfig;
            switch (strategy) {
                // Return everything from previous iterations, push override AND append current ID.
                case OverrideStrategy.Push: {
                    return [...acc, to, curr];
                }
                // Return everything from previous iterations, plus override, do NOT append the current ID.
                case OverrideStrategy.Replace: {
                    return [...acc, to];
                }
                // Return everything from previous iterations, plus current ID.
                default: {
                    return [...acc, curr];
                }
            }
        }, []);
        // Remove the surplus elements.
        return ids.slice(0, originalLength);
    }
    getPositionOfSet(set) {
        if (!this.hasPositionOfSet(set)) {
            this.positions[set] = 0;
        }
        return this.positions[set];
    }
    setPositionOfSet(set, newPositions) {
        this.positions[set] = newPositions;
    }
    hasPositionOfSet(set) {
        return typeof this.positions[set] === 'number';
    }
}
